<script setup>
import {TD, TR} from "@/Components/Table";
import ApplicationItem
    from "@/Pages/Education/ReceptionCompany/CompetitionLists/components/CompetitionListV2/ApplicationItem.vue";
import {computed} from "vue";
import {datetimeFormat} from "../../../../../../plugins/utils/time-utils";

const props = defineProps(['data', 'sortType']);

const isPay = computed(() => {
    return props.data?.concurs_group?.finance_level?.guid
        === 'af4b0b25-c722-11ee-80f5-00155d01193e';
});

const isTargeted = computed(() => {
    return props.data?.concurs_group?.type?.sys_name  === 'TARGET_RECEPTION'
        || props.data?.concurs_group?.type?.sys_name  === 'DETAILED_TARGET_RECEPTION';
});

const isSVE = computed(() => {
    return props.data?.concurs_group?.reception_company_type?.sys_name === 'SVE';
});

const sortApplicationsByRating = (a, b) => {
    if (a.position !== b.position) { return a.position > b.position ? 1 : -1; }
}

function sortApplications(a, b) {
    if(props.sortType === 'agree') {
        if(isPay.value) {
            const aAgreePoints = (Number(a?.payment?.state === 'Утвержден') * 2) + Number(a?.payment?.is_payment);
            const bAgreePoints = (Number(b?.payment?.state === 'Утвержден') * 2) + Number(b?.payment?.is_payment);

            if(aAgreePoints !== bAgreePoints) {
                return Number(aAgreePoints) > Number(bAgreePoints) ? -1 : 1;
            }
        }

        else {
            if(Number(a.agreement) !== Number(b.agreement)) {
                return Number(a.agreement) > Number(b.agreement) ? -1 : 1;
            }
        }

        return sortApplicationsByRating(a, b);
    }

    if(props.sortType === 'state') {
        const aP = applicationSortByStatePoints(a);
        const bP = applicationSortByStatePoints(b);

        if(aP !== bP) { return aP > bP ? -1 : 1; }

        return sortApplicationsByRating(a, b);
    }

    if(props.sortType === 'priority') {
        const aPriority = a?.priority?.priority ?? 0;
        const bPriority = b?.priority?.priority ?? 0;

        if(aPriority !== bPriority) {
            return aPriority > bPriority ? 1 : -1;
        }
    }

    if (props.sortType === 'upper-priority') {
        const aPriority = a?.priority?.upper ?? 0;
        const bPriority = b?.priority?.upper ?? 0;

        if(aPriority !== bPriority) {
            return aPriority > bPriority ? 1 : -1;
        }
    }

    if (props.sortType === 'original') {
        const aOriginal = Number(a.original);
        const bOriginal = Number(b.original);

        if(aOriginal !== bOriginal) {
            return aOriginal > bOriginal ? -1 : 1;
        }
    }

    if (props.sortType === 'agree-and-priority') {
        const calculatePoints = (application, isPay) => {
            const basicPoints = isPay
                ? ((Number((application?.payment?.state ?? null)  === 'Утвержден') * 200) + (Number(application?.payment?.is_payment ?? 0) * 100))
                : (Number(application?.agreement ?? 0) * 100);

            return basicPoints
                - Number(application?.priority?.priority ?? 0);
        };

        const aPoints = calculatePoints(a, isPay.value);
        const bPoints = calculatePoints(b, isPay.value);

        if(aPoints !== bPoints) {
            return aPoints > bPoints ? -1 : 1;
        }
    }

    else { return sortApplicationsByRating(a, b); }
}

function applicationSortByStatePoints(application) {
    switch (application.state) {
        case 'Зачислен': return 100;
        case 'Зачислен на другое направление': return 80;
        case 'Подано': return 0;
        case 'Выбыл из конкурса': return -100;
        default: return -10;
    }
}
</script>

<template>
    <TR class="border bg-gray-100">
        <TD class="border !font-bold" colspan="7">
            {{ data.concurs_group.type.name }}
            <template v-if="Boolean(data.concurs_group.is_foreign_citizen)">
                <br/>
                (Приём иностранных граждан)
            </template>

            <template v-if="data.concurs_group.sve_type">
                <br/>
                ({{ data.concurs_group.sve_type.name }})
            </template>

            <template v-if="!!data.date">
                <br/>
                <span class="text-[0.7rem] font-light text-gray-600">
                    <i class="fas fa-clock mr-1"/> Данные актуальны на: {{ datetimeFormat(data.date) }}
                </span>
            </template>
        </TD>
        <TD colspan="5" class="!py-1 !px-4 border !font-bold text-xs" v-html="data.concurs_group.specializations.map(el => el.name).join('<br/>')"/>
        <TD class="border !font-bold text-center">{{ data.concurs_group.places_count}}</TD>
    </TR>

    <TR v-if="!data.data?.length">
        <TD class="!px-2 !py-1 border text-center" colspan="11">Нет заявлений</TD>
    </TR>

    <template v-else>
        <tr class="border">
            <TD class="!px-2 !py-1 border !text-sm !font-bold text-center">№</TD>
            <TD :colspan="!isTargeted ? 2 : 1" class="!px-2 !py-1 border !text-sm !font-bold text-center">Уникальный код</TD>
            <TD v-if="isTargeted" class="!px-2 !py-1 border !text-sm !font-bold text-center">Номер предложения<br/>(Размещено на РВР)</TD>
            <TD v-if="!isPay || isSVE" :colspan="isSVE ? (isPay ? 1 : 3) : 4" class="!px-2 !py-1 border !text-sm !font-bold text-center">Согласие на зачисление</TD>
            <template v-if="isSVE">
                <TD class="!px-2 !py-1 border !text-sm !font-bold text-center">Оригинал</TD>
            </template>
            <template v-if="isPay">
                <TD :colspan="!isSVE ? 3 : 1" class="!px-2 !py-1 border !text-sm !font-bold text-center">Договор</TD>
                <TD class="!px-2 !py-1 border !text-sm !font-bold text-center">Оплачен</TD>
            </template>


            <TD class="!px-2 !py-1 border !text-sm !font-bold text-center" v-if="isSVE">Общий балл</TD>
            <TD class="!px-2 !py-1 border !text-sm !font-bold text-center">{{ isSVE ? 'Средний балл по аттестату' : 'Сумма баллов' }}</TD>
            <TD class="!px-2 !py-1 border !text-sm !font-bold text-center">По предметам</TD>
            <TD class="!px-2 !py-1 border !text-sm !font-bold text-center">Индивидуальные достижения</TD>
            <TD class="!px-2 !py-1 border !text-sm !font-bold text-center" v-if="!isSVE">Приоритет</TD>
            <TD class="!px-2 !py-1 border !text-sm !font-bold text-center">Комментарий</TD>
            <TD class="!px-2 !py-1 border !text-sm !font-bold text-center">Статус</TD>
        </tr>

        <ApplicationItem
            v-for="(application, index) in data.data.sort(sortApplications)"
            :key="`${data.concurs_group.guid}-${application.position}`"
            :cg-guid="data.concurs_group.guid"
            :is-pay="isPay"
            :is-targeted="isTargeted"
            :isSVE="isSVE"
            :is-sorted="sortType !== 'standard'"
            :position="index+1"
            :application="application"
        />
    </template>


</template>

<style scoped>

</style>
